import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ClientReviews,
  CompanyData,
  ContactDetails,
  DataSummary,
  FinancialData,
  FinancialDataAid,
  Needs,
  PartnersShowcase,
} from "./Onboarding";
import api from "../../api/resources";
import { useResults } from "../Financing/Assessment/Assessment";
import { useFormErrors } from "../commons/Form/Form";
import { useTranslation } from "react-i18next";
import { useToastsDispatch } from "../commons/Toasts/ToastsContext";

const clientImagesContext = import.meta.glob("../../assets/reviews/*.png", {
  eager: true,
});

export const OnboardingContext = createContext(null);

export function OnboardingProvider({
  children,
  assessment = false,
  onAssess,
  activeCompany = null,
}) {
  const { t } = useTranslation(["common", "onboarding"]);
  const dispatchToast = useToastsDispatch();

  const components = { Needs, FinancialData, CompanyData, ContactDetails };

  const asideComponents = {
    Needs: PartnersShowcase,
    FinancialData: FinancialDataAid,
    CompanyData: DataSummary,
    ContactDetails: ClientReviews,
  };

  const initialSteps = Object.entries(components).map(([id, component]) => ({
    id,
    component,
    asideComponent: asideComponents[id],
    done: false,
  }));

  const [steps, setSteps] = useState(initialSteps);

  const [selectedStep, setSelectedStep] = useState("Needs");

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    const newIndex = steps.findIndex((step) => step.id === selectedStep);
    setCurrentStepIndex(newIndex);
  }, [selectedStep, steps]);

  const handleSelect = (id) => setSelectedStep(id);

  const dataKeys = [
    "need",
    "amount",
    "turnover",
    "equity",
    "gross_operating_profit",
    "net_income",
    "company",
    "first_name",
    "surname",
    "email",
    "telephone",
    "ext_provider",
    "ext_id",
  ];

  const { errors, setErrors, clearErrors } = useFormErrors(
    dataKeys,
    "non_field_errors",
  );

  const turnoverMap = {
    less_150k: 149000,
    between_150k_300k: 150001,
    between_300k_500k: 300001,
    between_500k_1m: 500001,
    more_1m: 1000001,
    not_available: null,
  };

  const reverseTurnoverMapFn = (apiValue) => {
    if (apiValue === null) return "not_available";
    if (apiValue < 150000) return "less_150k";
    if (apiValue < 300000) return "between_150k_300k";
    if (apiValue < 500000) return "between_300k_500k";
    if (apiValue < 1000000) return "between_500k_1m";
    if (apiValue >= 1000000) return "more_1m";
    return null;
  };

  const genericMap = {
    positive: 1,
    negative: -1,
    not_available: null,
  };

  const reverseGenericMapFn = (apiValue) => {
    if (apiValue === null) return "not_available";
    if (apiValue < 0) return "negative";
    if (apiValue > 0) return "positive";
    return null;
  };

  const initFinancialData = (financialDataKey = "", map, reverseMapFn) => {
    if (!activeCompany) return null;

    const apiValue = activeCompany[financialDataKey];

    // API → map key
    const targetMapKey = reverseMapFn(apiValue);
    if (!targetMapKey) return null;

    // Map key → reverse map
    const optionValue = Object.entries(map).find(
      ([, mapValue]) => mapValue === map[targetMapKey],
    )[0];

    // Label
    const optionLabel = t(
      `onboarding:inputs.${financialDataKey}.options.${targetMapKey}`,
    );

    const initOption = {
      label: optionLabel,
      value: optionValue,
    };

    return initOption;
  };

  const location = useLocation();

  const getInitialData = () => {
    const initialData = {
      need: null,
      amount: null,
      turnover: initFinancialData(
        "turnover",
        turnoverMap,
        reverseTurnoverMapFn,
      ),
      equity: initFinancialData("equity", genericMap, reverseGenericMapFn),
      gross_operating_profit: initFinancialData(
        "gross_operating_profit",
        genericMap,
        reverseGenericMapFn,
      ),
      net_income: initFinancialData(
        "net_income",
        genericMap,
        reverseGenericMapFn,
      ),
      company: null,
      first_name: "",
      surname: "",
      email: "",
      telephone: "",
      ext_provider: null,
      ext_id: null,
      utm_source: "",
    };

    const dataFromURL = location.state?.urlData;

    return {
      ...initialData,
      ...dataFromURL,
    };
  };

  const [data, setData] = useState(getInitialData);

  const [userID, setUserID] = useState("");

  const { results, setResults } = useResults(null);

  const handleDataChange = (key, value) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const [resultsShown, setResultsShown] = useState(false);

  const handleEdit = () => {
    setSteps((prevSteps) =>
      prevSteps.map((step) =>
        step.id === selectedStep ? { ...step, done: false } : step,
      ),
    );
    setResultsShown(false);
  };

  const handleSubmitData = async () => {
    const handleDataConversion = (data) => {
      const {
        first_name,
        surname,
        email,
        telephone,
        ext_provider,
        ext_id,
        utm_source,
      } = data;

      const registrationData = {
        first_name,
        surname,
        email,
        telephone,
        ext_provider: ext_provider,
        ext_id: ext_id,
        company_code: data.company.code,
        turnover: turnoverMap[data.turnover.value],
        equity: genericMap[data.equity.value],
        gross_operating_profit: genericMap[data.gross_operating_profit.value],
        net_income: genericMap[data.net_income.value],
        registration_reason: { [data.need.value]: data.amount.value },
        source: utm_source,
      };
      return registrationData;
    };

    clearErrors();

    const registrationData = handleDataConversion(data);

    const response = await api.preRegister(registrationData);
    if (!response.ok) {
      // Account or company already existing
      const jsonErrors = await response.json();
      let allErrorsArray = [];
      for (const key of Object.keys(jsonErrors)) {
        allErrorsArray.push(jsonErrors[key]);
      }
      setErrors({
        non_field_errors: allErrorsArray,
      });
      return;
    }

    const { user_id, pre_eligibility } = await response.json();
    if (!user_id || !pre_eligibility) {
      dispatchToast({
        type: "add",
        variant: "info",
        subheading: t("common:toasts.errorToast.subheading"),
      });
      return;
    }
    setUserID(user_id);

    setResults(pre_eligibility);
    setResultsShown(true);
  };

  const handleSubmitDataForAssessment = async () => {
    const handleDataConversion = (data) => {
      return {
        turnover: turnoverMap[data.turnover.value],
        equity: genericMap[data.equity.value],
        gross_operating_profit: genericMap[data.gross_operating_profit.value],
        net_income: genericMap[data.net_income.value],
      };
    };

    clearErrors();
    const assessmentData = handleDataConversion(data);

    const response = await api.assessActiveCompany(assessmentData);
    if (!response.ok) {
      dispatchToast({
        type: "add",
        variant: "info",
        subheading: t("common:toasts.errorToast.subheading"),
      });
      const jsonErrors = await response.json();
      setErrors(jsonErrors);
      return;
    }

    const assessmentResults = await response.json();
    onAssess(assessmentResults);
  };

  const handleConfirm = () => {
    setSteps((prevSteps) => {
      const newSteps = prevSteps.map((step) =>
        step.id === selectedStep ? { ...step, done: true } : step,
      );

      const nextStep = newSteps[currentStepIndex + 1];

      if (nextStep) {
        setSelectedStep(nextStep.id);
      } else {
        if (newSteps.every((step) => step.done)) {
          handleSubmitData();
        }
      }

      return newSteps;
    });
  };

  const handleBack = () => {
    const previousStep = steps[currentStepIndex - 1];

    if (previousStep) {
      setSelectedStep(previousStep.id);
    }
  };

  const [signUpShown, setSignUpShown] = useState(false);

  const handleSignUp = async () => {
    const response = await api.activateRegistration(userID);
    if (!response.ok) {
      dispatchToast({
        type: "add",
        variant: "info",
        subheading: t("common:toasts.errorToast.subheading"),
      });
      return;
    }
    setSignUpShown(true);
  };

  const clientImgMap = generateImgMap(clientImagesContext);

  const [clientReviews] = useState(
    Object.keys(clientImgMap).map((clientKey) => ({
      id: clientKey,
      imgSrc: clientImgMap[clientKey],
      transKey: "onboarding:asides.ClientReviews." + clientKey,
    })),
  );

  const value = {
    steps,
    selectedStep,
    setSelectedStep,
    currentStepIndex,
    resultsShown,
    setResultsShown,
    handleSelect,
    handleEdit,
    handleConfirm,
    handleBack,
    data,
    handleDataChange,
    signUpShown,
    setSignUpShown,
    userID,
    results,
    setResults,
    handleSignUp,
    errors,
    assessment,
    handleSubmitDataForAssessment,
    clientReviews,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  return useContext(OnboardingContext);
}

export function generateImgMap(context) {
  const map = {};
  for (const path in context) {
    const fileName = path.split("/").pop();
    const key = fileName.split(".")[0];
    map[key] = context[path].default;
  }
  return map;
}
